import { useState } from "react";
import { useRoute, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  FileDown,
  Loader2,
  ArrowLeft,
  Building2,
  Calendar,
  AlertTriangle,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import ReportVisualization from "@/components/reports/ReportVisualization";
// @deprecated - Old client-side PDF generation (kept for reference)
// import { generatePdfReport } from "@/service/pdf-generator";
import { useGenerateTextReport } from "@/hooks/use-report-dashboard";
import { FileText } from "lucide-react";

interface ReportDashboardData {
  assessment: {
    id: string;
    organizationName: string;
    reportingYear: number;
    currency: string;
    sector: string;
    locked: boolean;
    workspaceName?: string;
  };
  carbonPerformance: {
    totalEmissionsTco2e: number;
    scope1Tco2e: number;
    scope2Tco2e: number;
    intensityMetrics: {
      emissionsPerRevenue: number;
      emissionsPerEmployee: number;
      energyIntensity: number;
    };
    reportingPeriod: string;
  };
  esgStatus: {
    environmental: {
      completeness: number;
      metricsProvided: number;
      totalMetrics: number;
    };
    social: {
      completeness: number;
      metricsProvided: number;
      totalMetrics: number;
    };
    governance: {
      completeness: number;
      metricsProvided: number;
      totalMetrics: number;
    };
  };
  trustIndicators: {
    completenessPercent: number;
    vsmeLevel: string;
    evidenceCount: number;
    dataQualityRating: number;
    boundaryStatement: string;
  };
  hotspots: Array<{
    source: string;
    tco2e: number;
    percentage: number;
  }>;
  recommendations: Array<{
    priority: string;
    title: string;
    description: string;
  }>;
}

export default function AdminReportDetail() {
  const [match, params] = useRoute("/admin/reports/:assessmentId");
  const assessmentId = params?.assessmentId;
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { i18n } = useTranslation();

  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const generateTextReport = useGenerateTextReport();

  const handleGenerateTextReport = async () => {
    if (!assessmentId || !reportData) return;
    try {
      const result = await generateTextReport.mutateAsync({ assessmentId, locale: i18n.language });
      setLocation("/report/text", {
        state: {
          report: {
            report: result.report,
            assessmentId,
            organizationName: reportData.assessment.organizationName,
            reportingYear: reportData.assessment.reportingYear.toString(),
          },
        },
      });
    } catch (error: any) {
      toast({
        title: "Failed to generate report",
        description: error.message || "An error occurred",
        variant: "destructive",
      });
    }
  };

  // Fetch report data for admin
  const {
    data: reportData,
    isLoading,
    error,
  } = useQuery<ReportDashboardData>({
    queryKey: ["admin-report-dashboard", assessmentId],
    queryFn: async () => {
      if (!assessmentId) {
        throw new Error("Assessment ID is required");
      }

      const response = await fetch(`/api/admin/reports/${assessmentId}/dashboard`, {
        credentials: "include",
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to fetch report data");
      }

      return response.json();
    },
    enabled: !!assessmentId,
    staleTime: 0,
    refetchOnWindowFocus: false,
    retry: false,
  });

  // Handle PDF generation (server-side via Puppeteer)
  const handleGeneratePdf = async () => {
    if (!reportData) return;

    setIsGeneratingPdf(true);
    try {
      const response = await fetch(`/api/admin/reports/${assessmentId}/pdf?locale=${encodeURIComponent(i18n.language)}`, {
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to generate PDF report");
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download =
        response.headers
          .get("Content-Disposition")
          ?.match(/filename="(.+)"/)?.[1] || `report_${assessmentId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);

      toast({
        title: "PDF Generated",
        description: "The report PDF has been generated successfully.",
      });
    } catch (error: any) {
      console.error("PDF generation error:", error);
      toast({
        title: "PDF Generation Failed",
        description: error.message || "Failed to generate PDF report",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto py-8 px-4 max-w-7xl">
        <div className="space-y-6">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto py-8 px-4 max-w-7xl">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>{"Error Loading Report"}</AlertTitle>
          <AlertDescription>
            {error instanceof Error ? error.message : "Failed to load report data"}
          </AlertDescription>
        </Alert>
        <Button
          onClick={() => setLocation("/admin/reports")}
          className="mt-4"
          variant="outline"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {"Back to Reports"}
        </Button>
      </div>
    );
  }

  if (!reportData) {
    return null;
  }

  return (
    <div className="container mx-auto py-8 px-4 max-w-7xl">
      <div className="space-y-6">
        {/* Header Section */}
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <Button
              onClick={() => setLocation("/admin/reports")}
              variant="ghost"
              size="sm"
              className="mb-2"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              {"Back to Reports"}
            </Button>
            <h1 className="text-3xl font-bold text-[#001F24]">
              {"Platform Admin - Report View"}
            </h1>
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                <span>{reportData.assessment.organizationName}</span>
              </div>
              {reportData.assessment.workspaceName && (
                <Badge variant="outline">
                  {reportData.assessment.workspaceName}
                </Badge>
              )}
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>
                  {`Reporting Year: ${reportData.assessment.reportingYear}`}
                </span>
              </div>
              <Badge variant={reportData.assessment.locked ? "default" : "secondary"}>
                {reportData.assessment.locked
                  ? "Locked"
                  : "Draft"}
              </Badge>
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleGenerateTextReport}
              disabled={generateTextReport.isPending}
            >
              {generateTextReport.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {"Generating..."}
                </>
              ) : (
                <>
                  <FileText className="mr-2 h-4 w-4" />
                  {"Generate Text Report"}
                </>
              )}
            </Button>
            <Button
              onClick={handleGeneratePdf}
              disabled={isGeneratingPdf}
              className="bg-[#00444C] hover:bg-[#003038]"
            >
              {isGeneratingPdf ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {"Generating PDF..."}
                </>
              ) : (
                <>
                  <FileDown className="mr-2 h-4 w-4" />
                  {"Generate PDF"}
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Info Card */}
        <Card className="border-[#7ECCC5] bg-blue-50">
          <CardHeader>
            <CardTitle className="text-[#009687]">{"Platform Administrator View"}</CardTitle>
            <CardDescription>
              {"You are viewing this report as a platform administrator. This report belongs to"}{" "}
              <strong>{reportData.assessment.organizationName}</strong>
              {reportData.assessment.workspaceName && (
                <>
                  {" "}
                  {"in workspace"}{" "}
                  <strong>{reportData.assessment.workspaceName}</strong>
                </>
              )}.
            </CardDescription>
          </CardHeader>
        </Card>

        {/* Report Visualization */}
        <ReportVisualization data={reportData} showStatusAlert={false} />
      </div>
    </div>
  );
}
